<template>
  <v-card :title="t('address')">
    <v-form @submit.prevent="submit">
      <v-card-text class="pt-0 pb-0">
        <v-row>
          <v-col class="pb-0">
            <v-text-field
              v-model="form.address.value.value"
              :error-messages="form.address.errorMessage.value"
              :label="t('insert_address')"
              density="compact"
            />
            <v-text-field
              v-model="form.city.value.value"
              :error-messages="form.city.errorMessage.value"
              :label="t('insert_city')"
              density="compact"
            />
            <v-text-field
              v-model="form.postal_code.value.value"
              :error-messages="form.postal_code.errorMessage.value"
              :label="t('postal_code')"
              density="compact"
            />
            <v-text-field
              v-model="form.country_alpha2.value.value"
              :error-messages="form.country_alpha2.errorMessage.value"
              :label="t('insert_country')"
              density="compact"
            />
            <v-btn @click="mySearchMapPosition" width="100%">{{ t('search') }}</v-btn>
            <v-divider class="mt-3 mb-3" thickness="3"/>
            <v-text-field
              v-model="form.latitude.value.value"
              :error-messages="form.latitude.errorMessage.value"
              type="number"
              :label="t('latitude')"
              density="compact"
            />
            <v-text-field
              v-model="form.longitude.value.value"
              :error-messages="form.longitude.errorMessage.value"
              type="number"
              :label="t('longitude')"
              density="compact"
            />
          </v-col>
          <v-col class="align-self-center pb-0">
            <v-container>
              <l-map :zoom="zoom" :center="[mapLat, mapLon]"
                     style="width: 100%; height: 380px; margin-left: auto; margin-right: auto;">
                <l-tile-layer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  layer-type="base"
                  name="OpenStreetMap"
                />
                <l-marker :visible="showMarker"
                          :lat-lng="[form.latitude?.value.value || 0, form.longitude?.value.value || 0]"
                          draggable @update:lat-lng="moveMarker"/>
              </l-map>
            </v-container>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn border type="submit">{{ t('save') }}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup>
import { ref, toRefs } from "vue";
import { useModifyParkBase } from "./base.js";
import * as yup from 'yup';
import { AddressNotFound, searchMapPosition } from "../../remote/map.js";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
// noinspection ES6UnusedImports
import L from "leaflet";

// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark']);
const { originalPark } = toRefs(props);
const emit = defineEmits(['updated']);

// Form
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  address: yup.string().required(),
  city: yup.string().required(),
  postal_code: yup.string().required(),
  country_alpha2: yup.string().required().length(2),
  latitude: yup.number().required().min(-90).max(90),
  longitude: yup.number().required().min(-180).max(180),
});

function specialSetter(form, newValue) {
  if (newValue !== null) {
    mapLat.value = form.latitude.value.value;
    mapLon.value = form.longitude.value.value;
    zoom.value = 14;
    showMarker.value = true;
  } else {
    showMarker.value = false;
  }
}

const { form, submit } = useModifyParkBase(
  useAlerts, loadingOverlay, originalPark, schema, emit, [], specialSetter
);

// Map State
const zoom = ref(4);
const mapLat = ref(47.41322);
const mapLon = ref(8.219482);
const showMarker = ref(false);

async function mySearchMapPosition() {
  // Validation check
  const v = await Promise.all([form.address.validate(), form.city.validate(), form.country_alpha2.validate(), form.postal_code.validate()]);
  if (!v.every(p => p.valid === true)) {
    console.log("Abort search map position for failed validation");
    return;
  }
  try {
    const { lat, lon } = await searchMapPosition(
      form.address.value.value.toString(),
      form.city.value.value.toString(),
      form.country_alpha2.value.value.toString(),
      form.postal_code.value.value.toString()
    );
    // Set Latitude and Longitude to the Marker and other things
    form.latitude.setValue(lat);
    form.longitude.setValue(lon);
    mapLat.value = lat;
    mapLon.value = lon;
    zoom.value = 14;
    showMarker.value = true;
  } catch (e) {
    if (e instanceof AddressNotFound) {
      useAlerts.value.warningAlert(t('no_address_found'), t('check_information'));
    } else {
      useAlerts.value.errorAlert(t('error_finding_address'), `${e.response?.status} ${e.response?.data}`);
    }
  }
}

function moveMarker(latlng) {
  // Trunc( N * 1e7) / 1e7 truncate at the 7th decimal place
  // form.latitude.setValue(Math.trunc(latlng.lat * 1e7) / 1e7);
  // form.longitude.setValue(Math.trunc(latlng.lng * 1e7) / 1e7);
  form.latitude.value.value = (Math.trunc(latlng.lat * 1e7) / 1e7);
  form.longitude.value.value = (Math.trunc(latlng.lng * 1e7) / 1e7);
}
</script>

<i18n>
{
  "en": {
    "address": "Address",
    "insert_address": "Insert your address [Rue Saint-Pierre 15]",
    "insert_city": "Insert your city [Paris]",
    "postal_code": "Postal Code [13035]",
    "insert_country": "Insert Country and click Search!",
    "search": "Search",
    "latitude": "Latitude",
    "longitude": "Longitude",
    "save": "Save",
    "no_address_found": "No address found",
    "check_information": "Please check your informations and try again.",
    "error_finding_address": "Error trying to find the address"
  },
  "it": {
    "address": "Indirizzo",
    "insert_address": "Inserisci il tuo indirizzo [Via Roma 15]",
    "insert_city": "Inserisci la tua città [Milano]",
    "postal_code": "Codice Postale [20100]",
    "insert_country": "Inserisci il Paese e clicca Cerca!",
    "search": "Cerca",
    "latitude": "Latitudine",
    "longitude": "Longitudine",
    "save": "Salva",
    "no_address_found": "Nessun indirizzo trovato",
    "check_information": "Controlla le tue informazioni e riprova.",
    "error_finding_address": "Errore durante la ricerca dell'indirizzo"
  },
  "fr": {
    "address": "Adresse",
    "insert_address": "Insérez votre adresse [Rue Saint-Pierre 15]",
    "insert_city": "Insérez votre ville [Paris]",
    "postal_code": "Code Postal [75001]",
    "insert_country": "Insérez le Pays et cliquez sur Rechercher!",
    "search": "Rechercher",
    "latitude": "Latitude",
    "longitude": "Longitude",
    "save": "Enregistrer",
    "no_address_found": "Aucune adresse trouvée",
    "check_information": "Veuillez vérifier vos informations et réessayer.",
    "error_finding_address": "Erreur lors de la recherche de l'adresse"
  }
}
</i18n>

<style scoped>

</style>
